import { useEffect, useState } from "react";
import { Users, Package } from "lucide-react";

import {
  Card,
  CardContent,
  KPICard,
  AdminRecentActivity,
  AdminQuickActions,
} from "@b2b/ui-components";
import { adminService, getAccessToken } from "@b2b/services";
import { useAuth } from "../../auth/Authprovider";

interface DashboardStatistics {
  totalUsers: number;
  activeSellers: number;
  totalProducts: number;
  changes: {
    users: { value: number; isPositive: boolean };
    sellers: { value: number; isPositive: boolean };
    products: { value: number; isPositive: boolean };
  };
  timestamp: string;
}

const AdminDashboard = () => {
  const { isLoading: authLoading, isAuthenticated } = useAuth();
  const [statistics, setStatistics] = useState<DashboardStatistics | null>(null);
  const [buyerMetrics, setBuyerMetrics] = useState<{
    activeBuyers: number;
    change: { value: number; isPositive: boolean };
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Wait for authentication to be ready before making API calls
    // This prevents API calls from being cancelled due to missing tokens
    const fetchStatistics = async () => {
      // Wait for auth to finish loading
      if (authLoading) {
        console.log("⏳ AdminDashboard: Waiting for authentication to be ready...");
        return;
      }

      // Check if user is authenticated
      if (!isAuthenticated) {
        console.log("⚠️ AdminDashboard: User not authenticated, skipping API calls");
        setLoading(false);
        return;
      }

      // Double-check that token exists before making API calls
      const token = await getAccessToken();
      if (!token) {
        console.log("⏳ AdminDashboard: Token not available yet, waiting...");
        // Retry after a short delay
        setTimeout(() => {
          fetchStatistics();
        }, 500);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        console.log("📞 AdminDashboard: Fetching dashboard data...");
        const [statsData, reportsData] = await Promise.all([
          adminService.getDashboardStatistics(),
          adminService.getReportsMetrics("30days").catch(() => null),
        ]);
        setStatistics(statsData);
        if (reportsData) {
          setBuyerMetrics({
            activeBuyers: reportsData.activeBuyers,
            change: reportsData.changes.buyers,
          });
        } else {
          setBuyerMetrics(null);
        }
        console.log("🚀 ~ fetchStatistics ~ statsData:", statsData);
        console.log("✅ AdminDashboard: Dashboard data fetched successfully");
      } catch (err: any) {
        console.error("❌ AdminDashboard: Error fetching dashboard statistics:", err);
        setError(err.message || "Failed to load dashboard statistics");
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, [authLoading, isAuthenticated]);

  const formatCount = (num: number): string => {
    return num.toLocaleString();
  };

  const kpiData = statistics
    ? [
        {
          title: "Total Users",
          value: formatCount(statistics.totalUsers),
          change: statistics.changes.users,
          icon: Users,
        },
        {
          title: "Active Sellers",
          value: formatCount(statistics.activeSellers),
          change: statistics.changes.sellers,
          icon: Package,
        },
       
        ...(buyerMetrics
          ? [
              {
                title: "Active Buyers",
                value: formatCount(buyerMetrics.activeBuyers),
                change: buyerMetrics.change,
                icon: Users,
              },
            ]
          : []),
        {
          title: "Total Products",
          value: formatCount(statistics.totalProducts),
          change: statistics.changes.products,
          icon: Package,
        },
      ]
    : [];

  console.log("kpiData", kpiData);

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <Card className="overflow-hidden bg-orange-500 mt-5">
        <CardContent className="p-0">
          <div className="relative h-48 lg:h-64 text-white">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-primary/70" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-primary-foreground space-y-4">
                <h1 className="text-4xl lg:text-5xl font-bold">
                  Welcome to MarketPlace
                </h1>
                <p className="text-xl opacity-90 max-w-2xl mx-auto px-4">
                  Your comprehensive B2B marketplace management platform
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      {loading ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
                <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card>
          <CardContent className="p-6">
            <div className="text-center text-red-600">
              <p className="font-medium">Error loading dashboard statistics</p>
              <p className="text-sm mt-2">{error}</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {kpiData.map((kpi, index) => (
            <KPICard
              key={index}
              title={kpi.title}
              value={kpi.value}
              change={kpi.change}
              icon={kpi.icon}
            />
          ))}
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column - Recent Activity */}
        <div className="lg:col-span-2 space-y-6">
          <AdminRecentActivity />
        </div>

        {/* Right Column - Quick Actions */}
        <div className="space-y-6">
          <AdminQuickActions />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
